import { useTransition } from '@react-spring/web';
import { createPortal } from 'react-dom';
import { animated } from '@react-spring/web';
import { ConfirmModal } from './ConfirmModal';
import { useEffect } from 'react';
import { formatLoggerData, logger } from 'services/logger';
import { eventBus } from 'services/eventBus';
import { quitCreateSupplementKnowledge } from 'services/api/supplementKnowledge';
import { useAuthorization } from 'components/Authorization';
import { useKnowledgeQueryCount } from 'hooks/business/im/useKnowledgeQuery';
import { useChatContext } from 'views/Conversation/Chat/contexts/ChatContext';
import { useQAPairMeta } from 'hooks/business/im/useQAPair';
import { ReWriteQuery } from './ReWriteQuery';
import { FingerIcon } from './FingerIcon';
import { useTranslate } from '@tolgee/react';

interface RelateModalProps {
  visible: boolean;
  conversation: Conversation.Conversation;
  knowledgeQuery: Conversation.KnowledgeQueryMessage;
  selectable: boolean;
  containerRef: React.RefObject<HTMLDivElement>;
}

export const RelateModal = (props: RelateModalProps) => {
  const { visible, conversation, knowledgeQuery, selectable, containerRef } = props;
  const { payload } = useAuthorization<Oversea.Token.Payload>();
  const { getKnowledgeQueryCount } = useKnowledgeQueryCount();
  const { setHasQAPairMounted } = useChatContext();
  const qaPairMeta = useQAPairMeta();
  const itemInfo = knowledgeQuery?.itemInfo;
  const { t } = useTranslate();

  const modalTransition = useTransition(visible, {
    from: {
      opacity: 0,
    },
    enter: {
      opacity: 1,
    },
    leave: {
      opacity: 0,
    },
    config: {
      duration: 250,
    },
  });

  useEffect(() => {
    setHasQAPairMounted(true);
    logger.info(
      `showRelateModal ${formatLoggerData({
        conversationId: conversation?.conversationId ?? '',
        queryConversationId: knowledgeQuery?.conversationId ?? '',
        queryMsgId: knowledgeQuery.msgId ?? '',
        timestamp: Date.now().toString(),
      })}`,
    );
    const handler = async ({ visible, msg }: { visible: boolean; msg: string }) => {
      const conversationId = conversation?.conversationId;
      if (conversationId) {
        const impressions = await getKnowledgeQueryCount(conversationId);
        quitCreateSupplementKnowledge({
          query_msg_id: knowledgeQuery?.msgId ?? '',
          popup_times: impressions,
          assistant_id: payload?.id,
          timestamp: Date.now(),
          exist_popup: visible,
          trigger_popup_msg: msg,
        });
      }
    };
    eventBus.on('CONFIRM_QAPAIR_MODAL_EXISTS', handler);
    return () => {
      eventBus.off('CONFIRM_QAPAIR_MODAL_EXISTS', handler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return modalTransition((style, item) => {
    const container = containerRef.current;
    if (!item || !container) {
      return null;
    }

    return createPortal(
      <animated.div
        style={style}
        className="absolute w-full left-0 top-0 z-20 flex items-start gap-4 pointer-events-none"
      >
        {qaPairMeta.source === 'AI_ERROR_REPLY' ? (
          <div className="max-w-[500px] min-w-[200px] flex items-center gap-2 min-h-[56px] bg-[var(--fett-bg-panel)] px-2 pointer-events-auto">
            <FingerIcon />
            <div>{t('请选择回复有误的买家问题')}</div>
          </div>
        ) : (
          <ReWriteQuery knowledgeQuery={knowledgeQuery} />
        )}
        <ConfirmModal
          knowledgeQuery={knowledgeQuery}
          conversation={conversation!}
          itemInfo={itemInfo}
          selectable={selectable}
        />
      </animated.div>,
      container,
    );
  });
};
